import axios from 'axios';
import logger from '../../utils/logger';
import AXIOS_BASE_CONFIG from './axiosConfigConstants';
// eslint-disable-next-line import/no-commonjs, global-require
const defaultAxiosConfig = require('./axiosDefaultConfig.json');

const bootstrapAxios = axios.create({
  timeout: AXIOS_BASE_CONFIG.jarvisBootstrapTimeoutMs,
});

function getConfigMap(configList, keyName, mergeObj = {}) {
  if (Array.isArray(configList)) {
    return configList.reduce((configMap, configItem) => {
      const configKey =
        configItem?.[keyName] || configItem?.domain || configItem?.urlPattern;

      if (configKey) {
        configMap[configKey] = { ...mergeObj, ...configItem };
      }

      return configMap;
    }, {});
  }

  return configList || {};
}

function buildActiveAxiosConfig(config) {
  const requestDefaultConfig = config?.requestDefaultConfig || {};
  const conectionDefaultConfig = config?.conectionDefaultConfig || {};
  return {
    requestURLConfigs: getConfigMap(
      config?.requestURLConfigs,
      'url',
      requestDefaultConfig,
    ),
    requestDomainConfigs: getConfigMap(
      config?.requestDomainConfigs,
      'domain',
      requestDefaultConfig,
    ),
    conectionDomainConfigs: getConfigMap(
      config?.conectionDomainConfigs,
      'domain',
      conectionDefaultConfig,
    ),
    requestDefaultConfig,
    conectionDefaultConfig,
    retryableStatusCodes: config?.retryableStatusCodes || [],
    retryableMethods: config?.retryableMethods || [],
  };
}

function hasAxiosConfig(config) {
  return (
    config?.requestDefaultConfig?.requestTimeout != null &&
    config?.conectionDefaultConfig?.connnectionTimeout != null
  );
}

let activeAxiosConfig = buildActiveAxiosConfig(defaultAxiosConfig);
let refreshTimer = null;
let loadPromise = null;

const configListeners = new Set();

export function getActiveAxiosConfig() {
  return activeAxiosConfig;
}

function notifyConfigListeners(nextConfig, previousConfig) {
  configListeners.forEach(listener => {
    try {
      listener(nextConfig, previousConfig);
    } catch (error) {
      logger.error('Axios config listener execution failed', error, {
        event: 'axios_config_listener_error',
      });
    }
  });
}

export function subscribeToAxiosConfigChanges(listener) {
  configListeners.add(listener);
  return () => configListeners.delete(listener);
}

function updateActiveAxiosConfig(config, shouldLog) {
  const previousConfig = activeAxiosConfig;
  activeAxiosConfig = buildActiveAxiosConfig(config);

  const nextConfig = activeAxiosConfig;

  if (shouldLog) {
    logger.info('Axios config loaded from Jarvis', {
      event: 'axios_config_loaded',
      request_url_rules: Object.keys(nextConfig.requestURLConfigs).length,
      request_domain_rules: Object.keys(nextConfig.requestDomainConfigs).length,
      connection_domain_rules: Object.keys(nextConfig.conectionDomainConfigs)
        .length,
    });
  }

  notifyConfigListeners(nextConfig, previousConfig);
}

async function fetchAndApplyAxiosConfig() {
  const configUrl = AXIOS_BASE_CONFIG.jarvisConfigUrl;

  try {
    const response = await bootstrapAxios.get(configUrl, {
      responseType: 'json',
    });

    if (!hasAxiosConfig(response.data)) {
      logger.warn('Jarvis returned incorrect axios config payload', {
        event: 'axios_config_empty_payload',
      });
      return;
    }

    updateActiveAxiosConfig(response.data, true);
  } catch (error) {
    logger.error('Axios config fetch from Jarvis failed', error, {
      event: 'axios_config_fetch_error',
      url: configUrl,
    });
  }
}

function scheduleAxiosConfigRefresh() {
  if (refreshTimer) {
    clearInterval(refreshTimer);
  }

  refreshTimer = setInterval(() => {
    fetchAndApplyAxiosConfig();
  }, AXIOS_BASE_CONFIG.jarvisRefreshIntervalMs);

  if (typeof refreshTimer.unref === 'function') {
    refreshTimer.unref();
  }
}

export function startAxiosConfigService() {
  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = fetchAndApplyAxiosConfig().finally(() => {
    scheduleAxiosConfigRefresh();
  });

  return loadPromise;
}

export function stopAxiosConfigService() {
  if (refreshTimer) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
}
